import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, take } from 'rxjs';

import { AppState } from 'src/app/core/app.state';
import { IUser } from 'src/app/features/settings/models/settings.models';
import * as settingsActions from 'src/app/features/settings/store/settings.actions';
import {
  getUserAvatarSrc,
  getUserColor,
  getUserFullname,
  getUserInitials,
} from 'src/app/shared/utils/user-color.util';

import { IconComponent } from '../icon/icon.component';

/**
 * Sticky footer pinned to the bottom of a sidebar: the signed-in user's avatar,
 * name and role, plus an icon-only logout action.
 *
 * Used by the main sidebar and the settings sidebar. The Smart Inbox sidebar
 * deliberately does not show it.
 *
 * Logout is emitted rather than handled here — the caller owns the sign-out
 * sequence (which preserves a few localStorage keys).
 */
@Component({
  selector: 'ui-sidebar-footer',
  standalone: true,
  imports: [CommonModule, TranslateModule, IconComponent],
  templateUrl: './sidebar-footer.component.html',
  styleUrls: ['./sidebar-footer.component.scss'],
})
export class SidebarFooterComponent implements OnInit {
  /** Icon-only presentation: the user block is hidden, logout remains. */
  @Input() collapsed = false;

  @Output() logout = new EventEmitter<void>();

  me$: Observable<IUser | null> = this.store.select((state) => state.settings.me);

  constructor(private store: Store<AppState>) {}

  ngOnInit(): void {
    // `me` is otherwise only fetched by the profile page, so the footer would
    // render empty until the user visited it. Fetch once if nothing has yet.
    this.me$.pipe(take(1)).subscribe((me) => {
      if (!me) {
        this.store.dispatch(settingsActions.GetMe());
      }
    });
  }

  fullname(user: IUser): string {
    return getUserFullname(user);
  }

  initials(user: IUser): string {
    return getUserInitials(user);
  }

  /** Stable per-user colour from the shared avatar palette. */
  avatarColor(user: IUser): string {
    return getUserColor(user);
  }

  /** First assigned role, shown under the name. */
  role(user: IUser): string {
    return user.roles?.[0]?.name ?? '';
  }

  /** Uploaded profile photo, or '' to fall back to coloured initials. */
  avatarSrc(user: IUser): string {
    return getUserAvatarSrc(user.image);
  }
}
